import { useState } from "react";
import { useAppDispatch } from "hooks";
import { Button, InlineStack, Text, Banner } from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { setIsModalxOpen } from "components/modalx/modalx.slice";
import { refuseFuelReport } from "services/fuel-report.service";
import { fetchFuelReportDetails } from "containers/fuel-report-view-container/fuel-report-container.slice";

interface RefuseFuelReportConfirmProps {
  reportId: string;
}

function RefuseFuelReportConfirm({ reportId }: RefuseFuelReportConfirmProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const [isLoading, setIsLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleConfirm = async () => {
    if (!companyId || !reportId) {
      setErrMsg("missing_required_data");
      return;
    }

    try {
      setIsLoading(true);
      setErrMsg("");
      setSuccessMsg("");

      await refuseFuelReport(companyId, reportId);

      setSuccessMsg("signatures_removed_successfully");
      // Refresh fuel report data
      dispatch(fetchFuelReportDetails({ companyId, reportId }));

      // Close modal after showing success message briefly
      setTimeout(() => {
        dispatch(setIsModalxOpen(false));
      }, 1500);
    } catch (error: any) {
      let errorMessage = "error_refusing_fuel_report";
      setErrMsg(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    dispatch(setIsModalxOpen(false));
  };

  return (
    <div className="refuse-fuel-report-confirm">
      {errMsg && (
        <div style={{ marginBottom: "16px" }}>
          <Banner tone="critical">{t(errMsg)}</Banner>
        </div>
      )}

      {successMsg && (
        <div style={{ marginBottom: "16px" }}>
          <Banner tone="success">{t(successMsg)}</Banner>
        </div>
      )}

      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <Text as="p" variant="bodyMd">
          {t("refuse_fuel_report_confirmation_message")}
        </Text>
      </div>

      <div style={{ marginTop: "20px" }}>
        <InlineStack gap="300" align="space-between">
          <Button
            onClick={handleConfirm}
            loading={isLoading}
            tone="critical"
            disabled={!!successMsg}
          >
            {t("refuse_report")}
          </Button>
          <Button onClick={handleCancel} disabled={isLoading}>
            {t("No")}
          </Button>
        </InlineStack>
      </div>
    </div>
  );
}

export default RefuseFuelReportConfirm;
